// services/lead-generation/compliance/ESGComplianceChecker.js

import { VSME_THRESHOLDS, CSRD_THRESHOLDS, HIGH_RISK_INDUSTRIES } from '../../../config/business-types/esg-compliance.config.js';

export class ESGComplianceChecker {
  /**
   * Check if company qualifies for VSME (Voluntary SME) reporting
   * @param {Object} company - Company data
   * @returns {Object} VSME qualification details
   */
  checkVSMEEligibility(company) {
    const { employees, turnover, balanceSheet } = company;
    
    const meetsEmployeeCriteria = employees >= VSME_THRESHOLDS.employees.min && 
                                 employees <= VSME_THRESHOLDS.employees.max;
    
    const meetsTurnoverCriteria = turnover >= VSME_THRESHOLDS.turnover.min && 
                                 turnover <= VSME_THRESHOLDS.turnover.max;
    
    const meetsBalanceSheetCriteria = balanceSheet >= VSME_THRESHOLDS.balanceSheet.min && 
                                     balanceSheet <= VSME_THRESHOLDS.balanceSheet.max;

    const qualificationCount = [meetsEmployeeCriteria, meetsTurnoverCriteria, meetsBalanceSheetCriteria]
      .filter(Boolean).length;

    return {
      isEligible: qualificationCount >= 2, // Need to meet at least 2 of 3 criteria
      criteria: {
        employees: meetsEmployeeCriteria,
        turnover: meetsTurnoverCriteria,
        balanceSheet: meetsBalanceSheetCriteria
      },
      qualificationScore: (qualificationCount / 3) * 100,
      priority: qualificationCount >= 2 ? 'MEDIUM' : 'LOW',
      opportunity: 'COMPETITIVE_ADVANTAGE',
      reasoning: this.generateVSMEReasoning(qualificationCount, company)
    };
  }

  /**
   * Check if company is subject to mandatory CSRD reporting
   * @param {Object} company - Company data
   * @returns {Object} CSRD compliance requirements
   */
  checkCSRDMandatory(company) {
    const { employees, turnover, balanceSheet, isListed, isEUSubsidiary } = company;
    
    // Large company criteria (any one triggers mandatory reporting)
    const isLargeCompany = employees > CSRD_THRESHOLDS.large.employees ||
                          turnover > CSRD_THRESHOLDS.large.turnover ||
                          balanceSheet > CSRD_THRESHOLDS.large.balanceSheet;

    // Listed SME criteria
    const isListedSME = isListed && employees >= CSRD_THRESHOLDS.listed.minEmployees;

    // Non-EU subsidiary of large groups
    const isNonEUSubsidiary = isEUSubsidiary && isLargeCompany;

    const isMandatory = isLargeCompany || isListedSME || isNonEUSubsidiary;
    
    return {
      isMandatory,
      category: this.getCSRDCategory(isLargeCompany, isListedSME, isNonEUSubsidiary),
      deadline: this.getCSRDDeadline(isLargeCompany, isListedSME, isNonEUSubsidiary),
      priority: isMandatory ? 'HIGH' : 'LOW',
      urgency: this.calculateUrgency(isLargeCompany, isListedSME, isNonEUSubsidiary),
      reasoning: this.generateCSRDReasoning(company, isLargeCompany, isListedSME)
    };
  }

  /**
   * Assess overall ESG reporting requirement for a company
   * @param {Object} company - Company data
   * @returns {Object} Complete ESG assessment
   */
  assessESGRequirement(company) {
    const vsmeAssessment = this.checkVSMEEligibility(company);
    const csrdAssessment = this.checkCSRDMandatory(company);
    const industryRisk = this.assessIndustryRisk(company.sicCode);
    
    // Determine primary ESG opportunity
    let primaryRequirement;
    let overallPriority;
    
    if (csrdAssessment.isMandatory) {
      primaryRequirement = 'CSRD_MANDATORY';
      overallPriority = 'HIGH';
    } else if (vsmeAssessment.isEligible) {
      primaryRequirement = 'VSME_VOLUNTARY';
      overallPriority = industryRisk.level === 'HIGH' ? 'MEDIUM-HIGH' : 'MEDIUM';
    } else {
      primaryRequirement = 'NOT_APPLICABLE';
      overallPriority = 'LOW';
    }

    return {
      primaryRequirement,
      overallPriority,
      vsme: vsmeAssessment,
      csrd: csrdAssessment,
      industryRisk,
      recommendations: this.generateRecommendations(primaryRequirement, industryRisk, company),
      timeline: this.generateTimeline(csrdAssessment, vsmeAssessment),
      businessValue: this.estimateBusinessValue(primaryRequirement, company)
    };
  }

  /**
   * Assess ESG risk level for specific industry
   * @param {string} sicCode - Standard Industry Classification code
   * @returns {Object} Industry risk assessment
   */
  assessIndustryRisk(sicCode) {
    if (!sicCode) return { level: 'UNKNOWN', score: 50, reasoning: 'No industry code provided' };
    
    const sicPrefix = sicCode.substring(0, 2);
    const industryMatch = HIGH_RISK_INDUSTRIES.find(industry => {
      const industryRange = industry.code.split('-');
      if (industryRange.length === 2) {
        return parseInt(sicPrefix) >= parseInt(industryRange[0]) && 
               parseInt(sicPrefix) <= parseInt(industryRange[1]);
      }
      return industry.code === sicPrefix;
    });

    if (industryMatch) {
      return {
        level: industryMatch.riskLevel,
        score: industryMatch.riskLevel === 'HIGH' ? 90 : 70,
        category: industryMatch.name,
        reasoning: `${industryMatch.name} sector has ${industryMatch.riskLevel.toLowerCase()} ESG reporting requirements`
      };
    }

    // Default for unlisted industries
    return {
      level: 'MEDIUM',
      score: 60,
      category: 'Other services',
      reasoning: 'Standard ESG reporting expectations for this industry'
    };
  }

  /**
   * Generate VSME reasoning text
   */
  generateVSMEReasoning(qualificationCount, company) {
    if (qualificationCount >= 2) {
      return `Company meets VSME criteria (${qualificationCount}/3 thresholds). Voluntary ESG reporting could provide competitive advantage and prepare for future mandatory requirements.`;
    }
    return `Company meets only ${qualificationCount}/3 VSME thresholds. May benefit from ESG readiness preparation.`;
  }

  /**
   * Generate CSRD reasoning text
   */
  generateCSRDReasoning(company, isLargeCompany, isListedSME) {
    if (isLargeCompany) {
      return `Company exceeds CSRD large company thresholds. Mandatory reporting required by 2024-2025.`;
    }
    if (isListedSME) {
      return `Listed SME company subject to CSRD reporting requirements from 2026.`;
    }
    return `Company not currently subject to mandatory CSRD reporting.`;
  }

  /**
   * Get CSRD category for company
   */
  getCSRDCategory(isLargeCompany, isListedSME, isNonEUSubsidiary) {
    if (isLargeCompany) return 'LARGE_COMPANY';
    if (isListedSME) return 'LISTED_SME';
    if (isNonEUSubsidiary) return 'NON_EU_SUBSIDIARY';
    return 'NOT_APPLICABLE';
  }

  /**
   * Get CSRD compliance deadline
   */
  getCSRDDeadline(isLargeCompany, isListedSME, isNonEUSubsidiary) {
    if (isLargeCompany) return '2024-2025';
    if (isListedSME) return '2026';
    if (isNonEUSubsidiary) return '2028';
    return null;
  }

  /**
   * Calculate urgency score based on deadlines
   */
  calculateUrgency(isLargeCompany, isListedSME, isNonEUSubsidiary) {
    const currentYear = new Date().getFullYear();
    
    if (isLargeCompany) {
      return currentYear >= 2024 ? 'IMMEDIATE' : 'HIGH';
    }
    if (isListedSME) {
      return currentYear >= 2025 ? 'HIGH' : 'MEDIUM';
    }
    if (isNonEUSubsidiary) {
      return currentYear >= 2027 ? 'MEDIUM' : 'LOW';
    }
    return 'LOW';
  }

  /**
   * Generate ESG recommendations based on assessment
   */
  generateRecommendations(primaryRequirement, industryRisk, company) {
    const recommendations = [];

    switch (primaryRequirement) {
      case 'CSRD_MANDATORY':
        recommendations.push('Immediate ESG reporting system implementation required');
        recommendations.push('Gap analysis against CSRD requirements');
        recommendations.push('Data collection and validation processes');
        if (industryRisk.level === 'HIGH') {
          recommendations.push('Industry-specific environmental impact assessment');
        }
        break;

      case 'VSME_VOLUNTARY':
        recommendations.push('Consider VSME framework adoption for competitive advantage');
        recommendations.push('ESG readiness assessment and roadmap');
        recommendations.push('Stakeholder engagement strategy');
        break;

      default:
        recommendations.push('ESG readiness evaluation for future compliance');
        break;
    }

    return recommendations;
  }

  /**
   * Generate compliance timeline
   */
  generateTimeline(csrdAssessment, vsmeAssessment) {
    const timeline = [];
    
    if (csrdAssessment.isMandatory) {
      timeline.push({
        phase: 'CSRD Compliance',
        deadline: csrdAssessment.deadline,
        urgency: csrdAssessment.urgency,
        actions: ['System implementation', 'Data collection', 'Report preparation']
      });
    }
    
    if (vsmeAssessment.isEligible) {
      timeline.push({
        phase: 'VSME Preparation',
        deadline: 'Voluntary',
        urgency: 'LOW',
        actions: ['Framework evaluation', 'Pilot reporting', 'Process optimization']
      });
    }

    return timeline;
  }

  /**
   * Estimate business opportunity value
   */
  estimateBusinessValue(primaryRequirement, company) {
    const baseValue = Math.min(company.turnover * 0.001, 50000); // 0.1% of turnover, max €50k
    
    const multipliers = {
      'CSRD_MANDATORY': 3.0,     // High value, mandatory purchase
      'VSME_VOLUNTARY': 1.5,     // Medium value, competitive advantage
      'NOT_APPLICABLE': 0.3      // Low value, future preparation
    };

    const estimatedValue = baseValue * (multipliers[primaryRequirement] || 1.0);
    
    return {
      estimated: Math.round(estimatedValue),
      confidence: primaryRequirement === 'CSRD_MANDATORY' ? 'HIGH' : 'MEDIUM',
      factors: ['Regulatory requirement', 'Company size', 'Industry complexity'],
      timeframe: primaryRequirement === 'CSRD_MANDATORY' ? '6-12 months' : '12-24 months'
    };
  }
}

// Export singleton instance
export const esgComplianceChecker = new ESGComplianceChecker();